import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  computed,
  OnInit,
  input,
  effect,
} from '@angular/core';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { ItdrService, PostureRiskCheck } from '../../services/identity-security.service';
import { RiskCheckCardComponent } from './components/risk-check-card/risk-check-card.component';
import { RiskCheckDetailComponent } from './components/risk-check-detail/risk-check-detail.component';
import { EntityStateChange } from './components/risk-check-detail/risk-check-detail.component';

@Component({
  selector: 'app-itdr-posture',
  standalone: true,
  imports: [CommonModule, RiskCheckCardComponent, RiskCheckDetailComponent],
  templateUrl: './itdr-posture.component.html',
  styleUrl: './itdr-posture.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItdrPostureComponent implements OnInit {
  private itdrService = inject(ItdrService);

  /** Deep-link target. When provided, pre-expand this check once data has loaded. */
  public targetCheckId = input<string | null>(null);

  /** Active organization, from `?organizationId=` on the page. */
  public organizationId = input<number | null>(null);

  public checksLoading = signal<boolean>(true);

  public riskChecks = signal<PostureRiskCheck[]>([]);
  public expandedRiskId = signal<string | null>(null);
  public acceptedCounts = signal<Record<string, number>>({});

  /**
   * Risk checks with the customer's shared severity-tier overrides applied, so a tier
   * change made in the score dialog shows here too (joined on check_id).
   */
  public displayChecks = computed<PostureRiskCheck[]>(() => this.riskChecks());

  constructor() {
    // Reload when the organization changes. switchMap so switching mid-flight cancels
    // the previous request: a late response would paint one customer's posture under
    // another customer's name.
    toObservable(this.organizationId)
      .pipe(
        tap(() => {
          this.riskChecks.set([]);
          this.checksLoading.set(true);
        }),
        switchMap((organizationId) => {
          if (organizationId == null) {
            return of([] as PostureRiskCheck[]);
          }
          return this.itdrService.getPostureRiskChecks(organizationId).pipe(
            catchError((err) => {
              console.error('Failed to load posture risk checks:', err);
              return of([] as PostureRiskCheck[]);
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe((res) => {
        this.riskChecks.set(res);
        this.checksLoading.set(false);
      });

    // When data finishes loading and a targetCheckId is set, expand it & scroll into view.
    effect(
      () => {
        const target = this.targetCheckId();
        const checks = this.riskChecks();
        if (!target || !checks.length) return;
        if (!checks.find((c) => c.check_id === target)) return;
        this.expandedRiskId.set(target);
        // Defer past this change-detection pass so the @for has actually rendered the
        // cards before we look one up. A microtask runs BEFORE that render on the first
        // data load, so getElementById returned null and the scroll silently no-oped --
        // the card expanded but the page never moved. setTimeout is a macrotask, which
        // runs after the DOM is updated.
        setTimeout(() => {
          const el = document.getElementById(`risk-check-${target}`);
          el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      },
      { allowSignalWrites: true }
    );
  }

  public getAcceptedCount(checkId: string): number {
    return this.acceptedCounts()[checkId] ?? 0;
  }

  ngOnInit(): void {
    // Loading lives in the constructor effect below, keyed on the organization.
    // ngOnInit stays so the component keeps its lifecycle contract.
  }

  public onToggleExpand(checkId: string): void {
    if (this.expandedRiskId() === checkId) {
      this.expandedRiskId.set(null);
    } else {
      this.expandedRiskId.set(checkId);
    }
  }

  public onEntityAction(event: EntityStateChange): void {
    this.acceptedCounts.update((counts) => ({
      ...counts,
      [event.checkId]: Math.max(0, (counts[event.checkId] ?? 0) + (event.accepted ? 1 : -1)),
    }));
  }

  public onAllAccepted(checkId: string): void {
    this.riskChecks.update((checks) =>
      checks.map((c) => (c.check_id === checkId ? { ...c, state: 'Accepted' as const } : c))
    );
  }

  public onCheckReopened(checkId: string): void {
    this.riskChecks.update((checks) =>
      checks.map((c) => (c.check_id === checkId ? { ...c, state: 'Open' as const } : c))
    );
  }
}
